/* Native Push registration for Capacitor (iOS APNs + Android FCM) via AWS SNS.

   How this fits with web push:
     - On a browser / PWA: useEffect early-returns; PushAutoEnablePrompt
       handles VAPID/Web Push.
     - Inside the Capacitor shell on iOS/Android: this hook requests
       notification permission, registers with APNs/FCM, then ships the
       device token to `POST /api/push/subscribe-native`. The backend
       calls SNS::CreatePlatformEndpoint and stores the resulting ARN.

   Lifecycle notes:
     - We call `register()` on every authenticated app boot. Per FCM &
       APNs guidance, tokens can rotate without the app being told, so
       re-registering on boot keeps the backend's mapping fresh.
     - Listeners are attached once per app session and torn down on
       logout so we don't double-fire push events when the user signs
       back in. */
import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { useUser } from "../context/UserAuthContext";
import { api } from "./api";

const PLATFORM_FROM_CAP = () => {
  // Capacitor returns "ios" | "android" | "web".
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android" ? p : null;
};

export default function useNativePush() {
  const { user, loading } = useUser();
  const registered = useRef(false);

  useEffect(() => {
    if (loading || !user) return;
    if (!Capacitor.isNativePlatform()) return;
    const platform = PLATFORM_FROM_CAP();
    if (!platform) return;

    let listeners = [];

    (async () => {
      let PushNotifications;
      try {
        // Dynamic import so web bundles never pull in the native module.
        ({ PushNotifications } = await import("@capacitor/push-notifications"));
      } catch (e) {
        console.warn("[native-push] plugin unavailable", e);
        return;
      }

      try {
        const perm = await PushNotifications.checkPermissions();
        if (perm.receive !== "granted") {
          const req = await PushNotifications.requestPermissions();
          if (req.receive !== "granted") {
            console.info("[native-push] user denied notifications");
            return;
          }
        }

        // Wire up listeners BEFORE register so we don't miss the very
        // first `registration` event on cold boots.
        listeners.push(
          await PushNotifications.addListener("registration", async (token) => {
            if (!token?.value) return;
            registered.current = true;
            try {
              let appInfo = {};
              try {
                appInfo = await CapApp.getInfo();
              } catch (_) { /* getInfo not supported on some platforms */ }
              await api.post("/push/subscribe-native", {
                kind: "native",
                platform,
                token: token.value,
                app_version: appInfo?.version || null,
                device_model: null,
                os_version: null,
              });
              console.info("[native-push] token registered with backend");
            } catch (e) {
              console.warn("[native-push] backend register failed", e);
            }
          })
        );

        listeners.push(
          await PushNotifications.addListener("registrationError", (err) => {
            console.error("[native-push] registration error", err);
          })
        );

        listeners.push(
          await PushNotifications.addListener(
            "pushNotificationReceived",
            (notification) => {
              // Foreground push — the OS doesn't show a banner by default
              // on iOS, so the app can decide what to do (toast, badge,
              // refetch, etc.). We keep the default behaviour minimal.
              console.info("[native-push] received foreground", notification);
            }
          )
        );

        listeners.push(
          await PushNotifications.addListener(
            "pushNotificationActionPerformed",
            (action) => {
              // User tapped the notification. Route them to the deep link
              // we packed into the payload's `data.link` (mirrors web SW).
              const link =
                action?.notification?.data?.link ||
                action?.notification?.data?.["data.link"] ||
                "/notifications";
              try {
                window.location.assign(link);
              } catch (_) { /* ignore */ }
            }
          )
        );

        await PushNotifications.register();
      } catch (e) {
        console.error("[native-push] init failed", e);
      }
    })();

    return () => {
      listeners.forEach((l) => {
        try { l?.remove?.(); } catch (_) { /* ignore */ }
      });
    };
  }, [user, loading]);
}
